import React, { useEffect, useState } from "react";
import { useRouter } from "../../hooks/useRouter";
import { useApolloClient } from "react-apollo";

import { isTokenExpired } from "../../helpers/isTokenExpired";
import { AuthContext, AuthContextType } from "./AuthContext";

// Queries
import { GET_VIEWER_QUERY } from "../../../queries/userQueries";
import {
  deleteLocalValue,
  getCachedUser,
  getLocalValues,
  setCachedUser,
  setLocalValue,
} from "../../helpers/localStorageHelpers";
import { getNewToken } from "../../../apollo/apolloHelpers";
import { QUERY_TTL } from "../../helpers/persistedQueryCache";
import { ExecutionResult } from "graphql";
import { Logo } from "../../../components/Logo/Logo";

interface AuthContextProviderProps {
  children: React.ReactNode | null;
}

export interface CategoryObject {
  id: string;
  databaseId: number;
  name: string;
  slug: string;
  termGroupId: number;
  description: string;
  parentId: number;
  count: number;
  menuOrder: string;
}

export interface AuthContextUserProps {
  id: string;
  email: string;
  userId: number;
  lastName: string;
  username: string;
  firstName: string;
  wishlist?: number[];
  showPrice?: boolean;
  showCategories?: CategoryObject[];
  showPriceCategories?: CategoryObject[] | false;
}

interface AuthContextProviderState {
  loading: boolean;
  user: AuthContextUserProps | undefined;
  error?: string | { [key: string]: string };
  loginError?: string | { [key: string]: string };
  registerError?: string;
}

const parseWishlist = (viewer: any): number[] => {
  const raw =
    viewer?.wishListProducts?.wishlistProducts ?? viewer?.wishListProducts;

  if (!raw) {
    return [];
  }

  if (Array.isArray(raw)) {
    return raw;
  }

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  return [];
};

const isValidCategory = (cat: any): cat is CategoryObject =>
  !!cat && cat.databaseId != null;

const hasPriceCategoryAccess = (
  showPriceCategories?: CategoryObject[] | false | null
): boolean =>
  Array.isArray(showPriceCategories) &&
  showPriceCategories.some((category) => category && category.databaseId != null);

export const normalizeCategoryList = (value: unknown): CategoryObject[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isValidCategory);
};

const toCategoryObject = (cat: any): CategoryObject | null => {
  if (!cat) {
    return null;
  }

  return {
    id: cat.term_taxonomy_id?.toString() || cat.id,
    databaseId: cat.term_id || cat.databaseId,
    name: cat.name,
    slug: cat.slug,
    termGroupId: cat.term_group || cat.termGroupId,
    description: cat.description,
    parentId: cat.parent || cat.parentId,
    count: cat.count,
    menuOrder: cat.term_order || cat.menuOrder,
  } as CategoryObject;
};

const resolveShowPrice = (
  showPrice: unknown,
  showPriceCategories: CategoryObject[] | false | undefined
): boolean => {
  if (showPrice === false) {
    return false;
  }
  return hasPriceCategoryAccess(showPriceCategories);
};

const mapViewerToUser = (
  viewer: any,
  fallback?: AuthContextUserProps
): AuthContextUserProps => {
  const showCategories = Array.isArray(viewer?.showCategories)
    ? normalizeCategoryList(viewer.showCategories)
    : normalizeCategoryList(fallback?.showCategories);
  const showPriceCategories = Array.isArray(viewer?.showPriceCategories)
    ? normalizeCategoryList(viewer.showPriceCategories)
    : normalizeCategoryList(fallback?.showPriceCategories);
  const showPrice = resolveShowPrice(
    Array.isArray(viewer?.showPriceCategories)
      ? viewer?.showPrice
      : viewer?.showPrice ?? fallback?.showPrice,
    showPriceCategories
  );

  const viewerWishlist = parseWishlist(viewer);

  return {
    id: viewer.id,
    email: viewer.email,
    userId: viewer.userId,
    lastName: viewer.lastName,
    username: viewer.username,
    firstName: viewer.firstName,
    wishlist: viewerWishlist.length ? viewerWishlist : fallback?.wishlist,
    showPrice,
    showCategories,
    showPriceCategories: showPrice ? showPriceCategories : [],
  };
};

export const AuthContextProvider = (props: AuthContextProviderProps) => {
  const router = useRouter();
  const client = useApolloClient();
  // const langPrefix = i18n.language === "sq" ? "" : "/" + i18n.language;
  const langPrefix = "/";

  const [state, setState] = useState<AuthContextProviderState>({
    loading: true,
    user: undefined,
    error: undefined,
    loginError: undefined,
    registerError: undefined,
  });

  //On route change: reset errors
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      error: undefined,
      loginError: undefined,
      registerError: undefined,
    }));
  }, [router.location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    checkAuthentication();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuthentication = async () => {
    const localValues = getLocalValues();
    const cachedUser = getCachedUser();

    if (!localValues.refreshToken || isTokenExpired(localValues.refreshToken)) {
      setState((prev) => ({
        ...prev,
        user: undefined,
        loading: false,
      }));
      return;
    }

    if (cachedUser) {
      const sanitizedCategories = normalizeCategoryList(
        cachedUser.showPriceCategories
      );
      const sanitizedUser = {
        ...cachedUser,
        showCategories: normalizeCategoryList(cachedUser.showCategories),
        showPriceCategories: sanitizedCategories,
        showPrice: resolveShowPrice(cachedUser.showPrice, sanitizedCategories),
      };
      setState((prev) => ({
        ...prev,
        user: sanitizedUser,
        loading: false,
      }));

      const viewerFetchedAt = Number(localValues.viewerFetchedAt || 0);
      if (
        viewerFetchedAt &&
        Date.now() - viewerFetchedAt < QUERY_TTL.viewer
      ) {
        return;
      }
    }

    try {
      if (!localValues.authToken || isTokenExpired(localValues.authToken)) {
        await getNewToken();
      }

      const res = await client.query({
        query: GET_VIEWER_QUERY,
        fetchPolicy: "network-only",
        errorPolicy: "all",
      });

      if (res?.data?.viewer) {
        const user = mapViewerToUser(res.data.viewer, cachedUser);
        setCachedUser(user);
        setLocalValue("viewerFetchedAt", String(Date.now()));
        setState((prev) => ({
          ...prev,
          user,
          error: undefined,
          loading: false,
        }));
        return;
      }
    } catch (err) {
      let errors: any = err;
      if (cachedUser) {
        setState((prev) => ({
          ...prev,
          error: errors?.toString?.(),
          user: cachedUser,
          loading: false,
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        error: errors?.toString?.(),
        loading: false,
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      user: cachedUser,
      loading: false,
    }));
  };

  const updateUser = (values: AuthContextUserProps) => {
    setCachedUser(values);
    setState((prev) => ({
      ...prev,
      user: {
        ...values,
      },
    }));
  };

  const login = async (
    res: {
      user: {
        id: string;
        authToken: string;
        email: string;
        firstName: string;
        lastName: string;
        username: string;
        userId: number;
        refreshToken: string;
        wishListProducts?: number[];
      };
      showCategories?: any[];
      showPriceCategories?: any[] | false;
      showPrice?: boolean;
      message?: string;
    },
    redirect: boolean = true
  ) => {
    if (!res?.user) {
      return;
    }

    const user = res.user;

    const wishlistProducts = res?.user?.wishListProducts || [];
    const loginUser = res?.user as {
      showCategories?: any[];
      showPriceCategories?: any[] | false;
      showPrice?: boolean;
    };

    const rawShowCategories =
      res?.showCategories || loginUser?.showCategories || [];
    const rawShowPriceCategories =
      res?.showPriceCategories === false ||
      loginUser?.showPriceCategories === false
        ? []
        : res?.showPriceCategories || loginUser?.showPriceCategories || [];

    const showCategories = normalizeCategoryList(
      Array.isArray(rawShowCategories)
        ? rawShowCategories.map(toCategoryObject)
        : []
    );

    const showPriceCategories = normalizeCategoryList(
      Array.isArray(rawShowPriceCategories)
        ? rawShowPriceCategories.map(toCategoryObject)
        : []
    );
    const showPrice = resolveShowPrice(
      res?.showPrice ?? loginUser?.showPrice,
      showPriceCategories
    );

    // TODO: Re check how the local storage values get deleted
    setLocalValue("authToken", user?.authToken);
    setLocalValue("refreshToken", user?.refreshToken);
    deleteLocalValue("wishlist");

    const nextUser = {
      id: user.id,
      email: user.email,
      userId: user.userId,
      lastName: user.lastName,
      username: user.username,
      firstName: user.firstName,
      wishlist: wishlistProducts,
      showPrice,
      showCategories: showCategories,
      showPriceCategories: showPrice ? showPriceCategories : [],
    };

    setCachedUser(nextUser);
    setLocalValue("viewerFetchedAt", String(Date.now()));
    setState((prev) => ({
      ...prev,
      loginError: undefined,
      user: nextUser,
    }));

    try {
      const viewerRes = await client.query({
        query: GET_VIEWER_QUERY,
        fetchPolicy: "network-only",
        errorPolicy: "all",
      });
      if (viewerRes?.data?.viewer) {
        const viewerUser = mapViewerToUser(viewerRes.data.viewer, nextUser);
        setCachedUser(viewerUser);
        setLocalValue("viewerFetchedAt", String(Date.now()));
        setState((prev) => ({
          ...prev,
          loginError: undefined,
          user: viewerUser,
        }));
      }
    } catch (e) {
      // Keep the login payload if the viewer query is unavailable.
    }

    const hasRedirectParam = router.location.search.includes("redirect");
    const returnURL =
      redirect && hasRedirectParam
        ? decodeURIComponent(router.location.search.split("redirect=")[1])
        : "/";

    router.history.push(returnURL);
  };

  const register = async (res: ExecutionResult) => {
    if (res.data?.registerCustomer) {
      const user = res.data?.registerCustomer.customer;

      const nextUser = {
        id: user.id,
        email: user.email,
        userId: user.databaseId,
        lastName: user.lastName,
        username: user.username,
        firstName: user.firstName,
      };

      setState((prev) => ({
        ...prev,
        registerError: undefined,
        user: nextUser,
      }));

      setLocalValue("authToken", res.data.registerCustomer.authToken);
      setLocalValue("refreshToken", res.data.registerCustomer.refreshToken);
      setCachedUser(nextUser);
      setLocalValue("viewerFetchedAt", String(Date.now()));

      const hasRedirectParam = router.location.search.includes("redirect");
      const returnURL = hasRedirectParam
        ? decodeURIComponent(router.location.search.split("redirect=")[1])
        : "/";

      router.history.push(returnURL);
    }
  };

  const logout = async () => {
    localStorage.removeItem("PROFITISHOP_USER");
    setState({
      ...state,
      user: undefined,
    });
    await client.cache.reset();
    router.history.push(langPrefix);
  };

  const context: AuthContextType = {
    isAuthenticated: state.user !== undefined,
    isLoading: state.loading,
    error: state.error,
    loginError: state.loginError,
    registerError: state.registerError,
    user: state.user,
    updateUser,
    logout: logout,
    checkAuthentication,
    login: login,
    register: register,
  };

  if (state.loading) {
    return (
      <div className="App App--loading">
        <Logo noLink />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={context}>
      {props.children}
    </AuthContext.Provider>
  );
};
